/**
 * Place-order API helper. Lifted from
 * `apps/customer-web/src/lib/checkout-order-submit.ts`.
 *
 * `source` is a typed literal union (`OrderSource`) — wrong values fail
 * typecheck instead of silently shipping. `submitPlaceOrder` takes a
 * configured `FetchWithClerk` instance (factory pattern from PR 3.0.5)
 * so each app supplies its own auth + base-URL wiring.
 */
import { COLORADO } from "@the-drop/types";
import { cartSubtotalCents, lineSubtotalCents } from "../cart/create-cart-store";
const CO_SURCHARGE = COLORADO.PURCHASE_LIMITS.CO_DELIVERY_SURCHARGE_CENTS;
export function buildPlaceOrderPayload(args) {
    const subtotalCents = cartSubtotalCents(args.lines);
    const lineItems = args.lines.map((l) => ({
        metrcPackageId: l.metrcPackageId,
        quantity: l.quantity,
        unitPriceCents: l.unitPriceCents,
        lineTotalCents: lineSubtotalCents(l),
    }));
    const taxCents = 0;
    const deliveryFeeCents = Math.max(0, Math.floor(args.customerDeliveryFeeCents));
    const totalCents = subtotalCents + taxCents + deliveryFeeCents + CO_SURCHARGE;
    const body = {
        dispensaryId: args.dispensaryId,
        deliveryAddressLine1: args.address.line1,
        deliveryCity: args.address.city,
        deliveryState: args.address.state,
        deliveryZip: args.address.zip,
        source: args.source,
        lineItems,
        subtotalCents,
        taxCents,
        deliveryFeeCents,
        coSurchargeCents: CO_SURCHARGE,
        totalCents,
    };
    if (args.address.line2.trim().length > 0) {
        body.deliveryAddressLine2 = args.address.line2.trim();
    }
    if (args.address.placeId && args.address.placeId.trim().length > 0) {
        body.deliveryPlaceId = args.address.placeId.trim();
    }
    if (args.deliveryPaymentMethod === "cash_at_delivery") {
        body.deliveryPaymentMethod = "cash_at_delivery";
    }
    else {
        if (args.straddlePaykey != null && args.straddlePaykey.trim().length > 0) {
            body.straddlePaykey = args.straddlePaykey.trim();
        }
        // Bank payment only. A cash order originates no ACH entry, so attaching an
        // authorization to one would record a consent that has no debit behind it.
        if (args.achAuthorization != null) {
            body.achAuthorization = args.achAuthorization;
        }
    }
    return body;
}
export class SubmitOrderHttpError extends Error {
    status;
    body;
    constructor(status, body) {
        super(`submit_order_http_${status}`);
        this.status = status;
        this.body = body;
        this.name = "SubmitOrderHttpError";
    }
}
export async function submitPlaceOrder(fetcher, getToken, body, args) {
    const res = await fetcher(getToken, "/api/v1/orders", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "Idempotency-Key": args.idempotencyKey,
        },
        body: JSON.stringify(body),
    });
    let parsed;
    try {
        parsed = await res.json();
    }
    catch {
        parsed = null;
    }
    if (!res.ok) {
        throw new SubmitOrderHttpError(res.status, parsed);
    }
    const o = parsed;
    if (typeof o.id !== "string") {
        throw new Error("order_response_invalid");
    }
    return {
        id: o.id,
        loadValueCheckPassed: o.loadValueCheckPassed === true,
    };
}
//# sourceMappingURL=place-order.js.map